import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import CommentsConfiguration from "./comments/CommentsConfiguration";
import GeoblockingConfiguration from "./GeoblockingConfiguration";
import GiftsConfiguration from "./GiftsConfiguration";
import LabelsConfiguration from "./LabelsConfiguration";
import LiveChatConfiguration from "./LiveChatConfiguration";
import PlayerConfiguration from "./PlayerConfiguration";
import PlaylistsConfiguration from "./PlaylistsConfiguration";
import SessionsMaxConfiguration from "./SessionsMaxConfiguration";
import UserRegistrationConfiguration from "./UserRegistrationConfiguration";
import ZapierSection from "./zapier/ZapierSection";
import { setProject as setProjectStore } from "../../../actions/architecture";
import ProjectAPI from "../../../api/ProjectAPI";
import { Service, Status } from "../../../api/ProjectAPI.types";
import { CONTENT_MAX_WIDTH_STYLE } from "../../../constants/size";
import "../../../css/admin/platform.scss";
import { email_regex } from "../../../utils/data/User";
import { isGeoblockActive } from "../../../utils/geoblock";
import { isValidHttpUrl } from "../../../utils/url";
import Breadcrumbs from "../../utils/Breadcrumbs";
import ContainerHeader from "../../utils/ContainerHeader";

import type { JSX } from "react";

import type {
  Project,
  StorefrontProjectParams,
} from "../../../api/ProjectAPI.types";
import type { AppState } from "../../../reducers";

function ConfigurationAdmin(): JSX.Element | null {
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const { t } = useTranslation("editor-platform");

  const getProject = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProject,
    params: [projectSlug],
  });

  const { data: getProjectData } = useQuery(getProject);

  const [project, setProject] = useState(getProjectData);

  useEffect(() => {
    if (getProjectData) {
      setProject(getProjectData);
    }
  }, [getProjectData]);

  const handleChangeProject = <K extends keyof Project>(
    key: K,
    value: Project[K],
  ): void => {
    setProject((prevProject) => {
      if (!prevProject) return prevProject;
      return {
        ...prevProject,
        [key]: value,
      };
    });
  };

  const handleChangeProjectParams = <
    K extends keyof Project["storefront_project_params"],
  >(
    key: K,
    value: Project["storefront_project_params"][K],
  ): void => {
    setProject((prevProject) => {
      if (!prevProject) return prevProject;
      return {
        ...prevProject,
        storefront_project_params: {
          ...prevProject.storefront_project_params,
          [key]: value,
        },
      };
    });
  };

  const updateProjectMutation = useMutation({
    mutationFn: async (data: Project) =>
      ProjectAPI.updateProject({
        projectSlug,
        data,
      }),
    onMutate: (data) => {
      const previousProject = queryClient.getQueryData([
        "getProject",
        projectSlug,
      ]);
      queryClient.setQueryData(["getProject", projectSlug], data);
      return { previousProject, project: data };
    },
    onSuccess: (_, __, context) => {
      if (context?.project) {
        dispatch(setProjectStore({ project: context?.project, projectSlug }));
      }
      toast.success(t("editor-notif:saveSuccess"));
    },
    onError: (_error, _variables, context) => {
      toast.error(t("editor-notif:saveError"));

      if (context?.previousProject) {
        queryClient.setQueryData(
          ["getProject", projectSlug],
          context.previousProject,
        );
      }
    },
  });

  const updateProject = (data: Project): void => {
    if (!projectSlug) return;
    updateProjectMutation.mutate(data);
  };

  const handleUpdateProject = (): void => {
    if (!project || !projectSlug) return;

    const {
      comment_abuse_email: commentAbuseEmail,
      geoblock_mode: geoblockMode,
      geoblock_redirect_url: geoblockRedirectUrl,
    } = project.storefront_project_params;

    const data = { ...project };
    if (data.triggers) {
      data.triggers = data.triggers.map((trigger) => ({
        email: trigger.email,
        event_name: trigger.event_name,
        zapier: trigger.zapier,
      }));
    }

    if (commentAbuseEmail && !commentAbuseEmail.match(email_regex)) {
      toast.error(t("error:errorValidationEmail"));
      return;
    }

    if (isGeoblockActive(geoblockMode)) {
      const trimmedRedirectUrl = geoblockRedirectUrl?.trim() ?? null;

      if (!trimmedRedirectUrl) {
        toast.error(t("error:errorValidationUrlRequired"));
        return;
      }

      if (!isValidHttpUrl(trimmedRedirectUrl)) {
        toast.error(t("error:errorValidationUrl"));
        return;
      }

      data.storefront_project_params = {
        ...project.storefront_project_params,
        geoblock_redirect_url: trimmedRedirectUrl,
      };
    }

    updateProject(data);
  };

  const getZapierKey = (proj: Project): { zapierKey: string } => {
    const zapierKeyData = proj.storefront_project_keys?.find(
      ({ service, status }) =>
        service === Service.Zapier && status === Status.Enabled,
    );
    return { zapierKey: zapierKeyData?.secret_key ?? "" };
  };

  const showZapierSuccessMessage = (text: string): void => {
    toast.success(text);
  };

  if (!project) return null;

  const { zapierKey } = getZapierKey(project);
  const {
    allow_multiple_playlists: allowMultiplePlaylists,
    allow_live_okast: allowLiveOkast,
    allow_zapier: allowZapier,
  } = orgaOptions;

  const {
    allow_player_mirror: allowPlayerMirror,
    display_free_label: displayFreeLabel,
    display_live_label: displayLiveLabel,
    display_replay_label: displayReplayLabel,
    display_top_label: displayTopLabel,
    period_top_label: periodTopLabel,
    display_new_label: displayNewLabel,
    period_new_label: periodNewLabel,
    display_last_chance_label: displayLastChanceLabel,
    period_last_chance_label: periodLastChanceLabel,
    display_tvod_label: displayTvodLabel,
    display_coming_soon_label: displayComingSoonLabel,
    period_coming_soon_label: periodComingSoonLabel,
    allow_chat_live: allowChatLive,
    livechat_display_user_count: livechatDisplayUserCount,
    require_registration_before_offer: requireRegistrationBeforeOffer,
    allow_upnext: allowUpnext,
    allow_preview: allowPreview,
    allow_media_resume: allowMediaResume,
    geoblock_mode: geoblockMode,
    countries: geoblockCountries,
    country_groups: geoblockCountryGroups,
    geoblock_redirect_url: geoblockRedirectUrl,
  } = project.storefront_project_params;

  const breadcrumbs = [
    {
      name: t("titleConfiguration"),
    },
  ];

  return (
    <div className="editor-platform editor-container editor-header-simple editor-has-breadcrumbs">
      <div
        className="mx-auto w-full max-w-(--maxWidth)"
        style={CONTENT_MAX_WIDTH_STYLE}
      >
        <Breadcrumbs items={breadcrumbs} />
        <ContainerHeader
          withBackBtn
          onSave={handleUpdateProject}
          item={{
            label: t("titleConfiguration"),
          }}
        />
        <div className="editor-form-container editor-tile-container">
          <UserRegistrationConfiguration
            onChange={handleChangeProjectParams}
            requireRegistrationBeforeOffer={requireRegistrationBeforeOffer}
          />
          <PlayerConfiguration
            hasAllowMediaResume={allowMediaResume}
            hasAllowPlayerMirror={allowPlayerMirror}
            hasAllowPreview={allowPreview}
            hasAllowUpnext={allowUpnext}
            onChange={handleChangeProjectParams}
          />
          <LabelsConfiguration
            hasDisplayComingSoonLabel={displayComingSoonLabel}
            hasDisplayFreeLabel={displayFreeLabel}
            hasDisplayLastChanceLabel={displayLastChanceLabel}
            hasDisplayLiveLabel={displayLiveLabel}
            hasDisplayNewLabel={displayNewLabel}
            hasDisplayReplayLabel={displayReplayLabel}
            hasDisplayTopLabel={displayTopLabel}
            hasDisplayTvodLabel={displayTvodLabel}
            onChange={handleChangeProjectParams}
            periodComingSoonLabel={periodComingSoonLabel}
            periodLastChanceLabel={periodLastChanceLabel}
            periodNewLabel={periodNewLabel}
            periodTopLabel={periodTopLabel}
          />
          <CommentsConfiguration
            params={project.storefront_project_params}
            onChange={<K extends keyof Project["storefront_project_params"]>(
              param: K,
              value: Project["storefront_project_params"][K],
            ): void => handleChangeProjectParams(param, value)}
          />
          <SessionsMaxConfiguration
            onChange={handleChangeProjectParams}
            sessionsMax={project.storefront_project_params.sessions_max || 3}
          />
          <PlaylistsConfiguration
            defaultChecked={project.storefront_project_params.playlists_type}
            hasAllowMultiplePlaylists={allowMultiplePlaylists}
            onChange={(
              value: StorefrontProjectParams["playlists_type"],
            ): void => {
              handleChangeProjectParams("playlists_type", value);
            }}
          />
          <LiveChatConfiguration
            hasAllowChatLive={allowChatLive}
            hasAllowLiveOkast={allowLiveOkast}
            hasLivechatDisplayUserCount={livechatDisplayUserCount}
            onChange={handleChangeProjectParams}
          />
          <GiftsConfiguration
            onChange={handleChangeProjectParams}
            project={project}
          />
          <GeoblockingConfiguration
            countries={geoblockCountries}
            countryGroups={geoblockCountryGroups}
            geoblockMode={geoblockMode}
            onChange={handleChangeProjectParams}
            redirectUrl={geoblockRedirectUrl ?? ""}
          />
          <ZapierSection
            apiKey={zapierKey}
            enabled={allowZapier}
            onSuccess={showZapierSuccessMessage}
            triggers={project.triggers}
            onChange={(triggers: Project["triggers"]): void =>
              handleChangeProject("triggers", triggers)
            }
          />
        </div>
      </div>
    </div>
  );
}

export default ConfigurationAdmin;
